import { mapActions, mapGetters, mapMutations } from 'vuex';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import { capitalizeFirstLetter } from '@/common/utils';
import _, { lowerCase, upperFirst } from 'lodash';
import { vnode } from '@/plans/EVENT_SAAS/helper/message';
import { billingCycleToValueMap } from '@/utils';
import PbxMixin from '@/plans/ZONE_PRO_PLUS/mixin/pbx-mixin';
const RelatedPlanType = [
  planTypes.ZOOM_MEETING,
  planTypes.ADDITIONAL_CO_EDITOR,
  planTypes.ADDITIONAL_HOST,
  planTypes.EVENT_SAAS,
  planTypes.EVENT_SAAS_CORE,
];
let messageBoxInstances = [];
export default {
  mixins: [PbxMixin],
  data() {
    return {
      cartInfoCache: {},
      showCycleAlert: false
    }
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'boughtPlans']),
    mx_licenseTips() {
      return this.$t('billing.events.saas.capacityTips', [this.title]);
    },
    mx_textViewModel() {
      return this.inVisibleCapacityList.length === 0;
    },
    hasBoughtBasePlan() {
      const { plans = [] } = this.getCurrentBasePlanInfo;
      if (plans?.[0]) {
        const hasBoughtBasePlan = this.boughtPlans.find(p => p.plan_code === plans[0].plan_code);
        if (hasBoughtBasePlan) {
          return true;
        }
      }
      return false;
    }
  },
  mounted() {
    if (this.onlyMixinsFunc) {
      return;
    }
    this.setReadOnlyCycleModel();
  },
  methods: {
    ...mapActions(['switchBillingCycleAction']),
    ...mapMutations(['removePlan', 'setGlobalStatus','setPbxBasePlanCode']),
    // Classified by billing cycle
    mx_getCurrentCapacityGroupOptions(options = []) {
      const group = {};
      const cycleLabelMap = {
        [BILLING_CYCLE.MONTHLY]: `${this.$t('billing.billing_cycle.monthly')} ${this.$t('billing.billing_cycle.subscription')}`,
        [BILLING_CYCLE.ANNUAL]: `${capitalizeFirstLetter(this.$t('billing.billing_cycle.annual'))} ${this.$t('billing.billing_cycle.subscription')}`,
      }
      options.forEach(capacity => {
        const cycleOption = this.capacityMap?.[capacity];
        if(cycleOption) {
          cycleOption.forEach(plan => {
            if (group[plan.billing_cycle]) {
              group[plan.billing_cycle].push(plan);
            } else {
              group[plan.billing_cycle] = [plan];
            }
          })
        }
      });
      const groups = Object.keys(group).reduce((prev, key) => {
        const cur = group[key];
        const label = cycleLabelMap[key];
        prev.push({
          label,
          cycle: key,
          options: cur.map(p => ({ capacity: p.capacity }))
        });
        return prev;
      }, []);
      return groups;
    },
    // Classified by capacity
    mx_getBillingCycleGroupOptions(_, capacity) {
      const isCurrentSelectedCapacityPlan = this.selectedPlanList?.find?.(p => p.capacity === capacity);
      const categorizedPlanByCycle = this.getCategorizedPlanByCycle(capacity);
      const options = categorizedPlanByCycle.map(p => {
        const sortOptions = p.options.sort((a,b) => a.capacity - b.capacity);
        const maxCapacity = sortOptions[sortOptions.length - 1]?.capacity;
        const minCapacity = sortOptions[0]?.capacity || 100;
        const label = maxCapacity > minCapacity ? this.$t('billing.single_use_meeting.a_to_b_attendees', [minCapacity, maxCapacity]) : `${minCapacity} ${upperFirst(this.$t('billing.attendees'))}`;
        // filter unselected plan
        const availableSortOptions = sortOptions.filter(plan => {
          const hasSelected = this.selectedPlanList.find(p => plan.plan_code === p.plan_code);
          return !hasSelected;
        });
        let availableOption = null;
        if (isCurrentSelectedCapacityPlan && isCurrentSelectedCapacityPlan.billing_cycle === p.cycle && sortOptions.find(o => o.capacity === capacity)) {
          availableOption = this.mx_getBillingCycleList(capacity);
        } else if (availableSortOptions.length) {
          availableOption = this.mx_getBillingCycleList(availableSortOptions[0]?.capacity);
        }
        if (availableOption.length) {
          // add2cart and bought base plan, trial2paid, zs2ze flow only display available plan
          if ((this.$context.is.add2cart && this.hasBoughtBasePlan) || this.$context.is.trial2paid || this.$context.is.zs2ze) {
            availableOption = availableOption
              .filter(o => o.plan.billing_cycle === p.cycle && !o.disabled);
          }
          // add2cart and not bought base plan, basic2pro, buy now flow, customer can buy all plans, set disabled to false
          if (this.$context.is.buyNow || this.$context.is.basic2pro || this.$context.is.add2cart) {
            availableOption = availableOption
              .filter(o => o.plan.billing_cycle === p.cycle)
              .map(plan => {
                if (plan.disabled) {
                  return {
                    ...plan,
                    disabled: false,
                    tooltips: '',
                  }
                }
                return plan;
              });
          }
        }
        return {
          label,
          options: availableOption?.length ? availableOption : [],
          cycle: p.cycle,
        }
      });
      return options;
    },
    getCategorizedPlanByCycle(capacity) {
      const availablePlanList = this.planList.filter(p => {
        const isSelected = this.selectedPlanList.find(plan => p.capacity == plan.capacity && capacity !== plan.capacity);
        return !isSelected;
      });
      const categorizedPlanByCycle = availablePlanList.reduce((prev, cur) => {
        const target = prev.find(p => p.cycle === cur.billing_cycle);
        if (target) {
          target.options.push(cur);
        } else {
          prev.push({
            cycle: cur.billing_cycle,
            options: [cur]
          })
        }
        return prev;
      }, []);
      return categorizedPlanByCycle;
    },
    async handleAddCapacity(event) {
      const nextCapacity = this.nextCapacity;
      const nextPlan = this.planList.find(p => p.capacity === nextCapacity);
      if (nextPlan?.plan_code) {
        await this.checkPlanIsAvailable(nextPlan);
        this.$nextTick(() => {
          this.updateSelectPlan({
            plan_code: nextPlan.plan_code,
            cartInfo: {
              selected_number: 1,
            },
          });
        });
      };
      this.visibleCapacityList = _.concat(this.visibleCapacityList, nextCapacity);
      this.afterAddCapacityHook(nextCapacity);
      this.handleAddCapacityTracking(event, nextCapacity);
    },
    setCurrentRelatedPlanCartInfo() {
      const currentRelatedSelectedPlans = this.getSelectedPlans
        .map(p => ({type: p.type, category: p.category, cartInfo: {...p.cartInfo}, plan_code: p.plan_code, cycle: p.billing_cycle, capacity: p.capacity}))
        .filter(p => RelatedPlanType.includes(p.type));
      const categorizedSelectedPlanByType = currentRelatedSelectedPlans.reduce((prev, cur) => {
          if (prev[cur.type]) {
            prev[cur.type].push(cur);
          } else {
            prev[cur.type] = [cur];
          }
          return prev;
        }, {});
      return categorizedSelectedPlanByType;
    },
    setReadOnlyCycleModel() {
      // only available in buy now / basic to pro / add to cart flow
      this.setGlobalStatus({isReadOnlyCycleModel: this.$context.is.buyNow || this.$context.is.basic2pro || this.$context.is.add2cart});
    },
    // before switch cycle or switch capacity, should check if the base plan cycle value is more than addon plan cycle value
    async checkPlanIsAvailable({ plan_code, billing_cycle, capacity }) {
      const capacityOptions = this.mx_getBillingCycleList(capacity);
      const targetOption = capacityOptions.find(o => o.plan.plan_code === plan_code);
      this.cartInfoCache = this.setCurrentRelatedPlanCartInfo();
      await this.checkBasePlanIsAvailable({targetOption, plan_code, billing_cycle, capacity});
      await this.checkAddonIsAvailable({targetOption, plan_code, billing_cycle, capacity});
    },
    async checkBasePlanIsAvailable({ targetOption, billing_cycle }) {
      if (this.hasBoughtBasePlan) {
        return Promise.resolve();
      }
      if (targetOption?.disabled && billing_cycle === BILLING_CYCLE.ANNUAL) {
        await this.switchBaseBillingCycleAndBanner(billing_cycle,true);
      }
      return Promise.resolve();
    },
    async switchBaseBillingCycleAndBanner(billing_cycle, isOpenTip = true) {
      // update pro plan from M2A
      const { plans = [] } = this.getCurrentBasePlanInfo;
      const { type, category, plan_code, cartInfo, planNameKey } = plans?.[0] ?? {};
      const basePlan = this.planMapFlat.find(p => p.type === type && p.category === category && billing_cycle === p.billing_cycle);
      if (basePlan?.plan_code) {
        await this.switchBillingCycleAction([plan_code, { ...basePlan, cartInfo }]);
        if ([planTypes.ZONE_PRO_PLUS, planTypes.ZONE_EDU_PLUS].includes(this.getCurrentBasePlanInfo.planType)) {
          this.switchPbxAddonAndBasePbx(billing_cycle);
        }
        if (isOpenTip) {
          await this.showMessageBox({
            mainPlanName: this.$t(planNameKey),
            mainPlanCycle: lowerCase(this.$t(`billing.cycle.${basePlan.billing_cycle}`)),
            subPlanName: this.title,
            subPlanCycle: lowerCase(this.$t(`billing.cycle.${billing_cycle}`)),
          });
        }
      }
    },
    async checkAddonIsAvailable({ targetOption, billing_cycle }) {
      if (targetOption?.disabled) {
        const plans = this.getSelectedPlans.filter(p => [planTypes.ADDITIONAL_HOST, planTypes.ADDITIONAL_CO_EDITOR].includes(p.type));
        if (plans.length) {
          for (let i = 0; i < plans.length; i++) {
            const plan = plans[i];
            const { type, category, plan_code, cartInfo, planNameKey, cycleValue } = plan || {};
            const basePlan = this.planMapFlat.find(p => p.type === type && p.category === category && billing_cycle === p.billing_cycle);
            if (basePlan?.plan_code && billingCycleToValueMap[billing_cycle] < cycleValue) {
              await this.switchBillingCycleAction([plan_code, { ...basePlan, cartInfo }]);
              await this.$nextTick();
              await this.showMessageBox({
                mainPlanName: this.$t(planNameKey),
                mainPlanCycle: lowerCase(this.$t(`billing.cycle.${basePlan.billing_cycle}`)),
                subPlanName: this.title,
                subPlanCycle: lowerCase(this.$t(`billing.cycle.${billing_cycle}`)),
              });
            }
          }
        }
      }
      return Promise.resolve();
    },
    async beforeSwitchCapacityHook(oldSelectedPlan, newPlanInfo) {
      await this.checkPlanIsAvailable(newPlanInfo);
      return oldSelectedPlan?.cartInfo;
    },
    async mx_beforeSwitchCycle(_, newPlan) {
      if (newPlan?.plan_code) {
        await this.checkPlanIsAvailable(newPlan);
      }
      return Promise.resolve();
    },
    async showMessageBox({ mainPlanName, mainPlanCycle, subPlanName, subPlanCycle }) {
      const h = this.$createElement;
      const mes = this.$t('billing.message.billing_cycle_update_alert', [mainPlanName, mainPlanCycle, subPlanName, subPlanCycle]);
      const instance = this.$message({
        message: vnode(mes, h, this.undoChange),
        showClose: true,
        iconClass: 'zm-icon-info',
        dangerouslyUseHTMLString: true,
        duration: 6000,
        customClass: 'zoom-one-boundle__auto-add',
      });
      messageBoxInstances.push(instance);
      return instance;
    },
    // Only record the last change, unchange the last change
    undoChange() {
      if (Object.keys(this.cartInfoCache).length) {
        const cartInfo = this.setCurrentRelatedPlanCartInfo();
        Object.keys(cartInfo).forEach((type) => {
          const currentCartInfo = cartInfo[type];
          const newCartInfo = this.cartInfoCache[type];
          currentCartInfo.forEach((oldPlan, index) => {
            const newPlan = newCartInfo[index];
            if (!newPlan?.plan_code) {
              this.removePlan(oldPlan.plan_code);
              this.visibleCapacityList = _.filter(this.visibleCapacityList, (c) => c !== oldPlan.capacity);
            } else if (oldPlan.plan_code !== newPlan?.plan_code) {
              this.switchBillingCycleAction([oldPlan.plan_code, { ...newPlan }]);
              if (oldPlan?.capacity && newPlan?.capacity) {
                const oldIndex = this.visibleCapacityList.indexOf(oldPlan.capacity);
                if (!this.visibleCapacityList.includes(newPlan.capacity)) {
                  this.visibleCapacityList.splice(oldIndex, 1, newPlan.capacity);
                }
              }
            }
          });
        });
        this.cartInfoCache = {};
      }
      if ([planTypes.ZONE_PRO_PLUS, planTypes.ZONE_EDU_PLUS].includes(this.getCurrentBasePlanInfo.planType)) {
        this.switchPbxAddonAndBasePbx(BILLING_CYCLE.MONTHLY);
      }
      this.clearMessageBoxInstances()
    },
    clearMessageBoxInstances() {
      messageBoxInstances.forEach(ins => {
        ins?.close?.();
      });
      messageBoxInstances = [];
    },
    mx_getAnnualPlan(capacityPlanList = []) {
      let annualDisabledAlert = '';
      if (this.mx_annualPlanDisabled.disabled && this.$context.is.add2cart && this.hasBoughtBasePlan) {
        this.showCycleAlert = true;
        annualDisabledAlert = this.mx_annualPlanDisabled.reason;
      }
      return {
        plan: _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.ANNUAL),
        disabled: this.mx_annualPlanDisabled.disabled,
        tooltips: this.mx_annualPlanDisabled.reason,
        alert: annualDisabledAlert,
      };
    },
  }
}
